import { filePath, type VaultService } from './vault.http'

export const SOURCE_REVIEW_TERMS = 'source-reconciliation.v1'
export type ReviewState = 'pending' | 'approved' | 'rejected' | 'cancelled' | 'revoked'
export type ReviewAction = 'approve' | 'reject' | 'cancel' | 'revoke'
export type ReviewReason =
  | 'ambiguous_identity'
  | 'metadata_correction'
  | 'undated'
  | 'classification_review'
export interface SourceAliasChoice {
  source_alias_id: string
  customer_type: 'individual' | 'business'
  customer_id: number
}
export interface SourceReviewSelection extends SourceAliasChoice {
  classification: 'client_controlled' | 'client_output'
  service: VaultService
  category: string
  financial_year: string | null
  period_basis: 'financial_year' | 'undated'
  reason: ReviewReason
  confirm_nonprivate_source: boolean
  confirm_client_ownership: boolean
  terms_version: typeof SOURCE_REVIEW_TERMS
}
export interface SourceReviewStatus {
  review_id: string
  client_id: string
  state: ReviewState
  decision_id: string | null
}
export interface SourceReviewItem extends SourceReviewStatus {
  proposed_at: string
  classification: SourceReviewSelection['classification']
  service: VaultService
  category: string
  financial_year: string | null
  reason: ReviewReason
}
export interface SourceReviewInspection extends SourceReviewStatus {
  selection: SourceReviewSelection
  selection_sha256: string
  source_snapshot_sha256: string
  proposed_at: string
}

export interface SourceDocumentChoice {
  source_document_id: string
  filename: string
  source_snapshot_sha256: string
  reviewable: boolean
  status: 'requires_owner_review' | 'requires_source_mapping' | 'requires_content_review'
}
export interface SourceDocumentsPage {
  client_id: string
  items: SourceDocumentChoice[]
  total: number
  page: number
  page_size: number
}
export interface SourceAdoptionDescriptor {
  client_id: string
  review_id: string
  decision_id: string
  source_document_id: string
  source_snapshot_sha256: string
  selection_sha256: string
  metadata: Pick<
    SourceReviewSelection,
    'classification' | 'service' | 'category' | 'financial_year'
  >
}
export interface ReviewedCopyCommand {
  source_document_id: string
  source_review_id: string
  source_decision_id: string
  approve_portability: true
  metadata: SourceAdoptionDescriptor['metadata'] & { idempotency_key: string }
}
export interface ReviewedCopyReceipt {
  operation_id: string
  client_id: string
  resource_id: string
  version_id: string
  scan_job_id: string
  state: 'pending_scan'
}

const exactKeys = (value: object, keys: string[]) =>
  Object.keys(value).length === keys.length &&
  keys.every(key => Object.prototype.hasOwnProperty.call(value, key))

export function validAliases(value: { items: SourceAliasChoice[] } | undefined): boolean {
  return (
    !!value &&
    exactKeys(value, ['items']) &&
    Array.isArray(value.items) &&
    value.items.length <= 100 &&
    value.items.every(
      item =>
        !!item &&
        exactKeys(item, ['source_alias_id', 'customer_type', 'customer_id']) &&
        isUuid(item.source_alias_id) &&
        ['individual', 'business'].includes(item.customer_type) &&
        Number.isInteger(item.customer_id) &&
        item.customer_id > 0 &&
        item.customer_id <= 2147483647
    ) &&
    new Set(value.items.map(item => item.source_alias_id)).size === value.items.length &&
    new Set(value.items.map(item => `${item.customer_type}:${item.customer_id}`)).size ===
      value.items.length
  )
}

export function validSources(
  value: SourceDocumentsPage | undefined,
  client: string,
  page: number
): value is SourceDocumentsPage {
  return (
    !!value &&
    exactKeys(value, ['client_id', 'items', 'total', 'page', 'page_size']) &&
    value.client_id === client &&
    value.page === page &&
    value.page_size === 20 &&
    Number.isInteger(value.total) &&
    value.total >= 0 &&
    Array.isArray(value.items) &&
    value.items.length <= Math.min(20, value.total) &&
    new Set(value.items.map(item => item?.source_document_id)).size === value.items.length &&
    value.items.every(
      item =>
        !!item &&
        exactKeys(item, [
          'source_document_id',
          'filename',
          'source_snapshot_sha256',
          'reviewable',
          'status',
        ]) &&
        isUuid(item.source_document_id) &&
        typeof item.filename === 'string' &&
        item.filename.length > 0 &&
        item.filename.length <= 255 &&
        /^[a-f0-9]{64}$/.test(item.source_snapshot_sha256) &&
        typeof item.reviewable === 'boolean' &&
        ['requires_owner_review', 'requires_source_mapping', 'requires_content_review'].includes(
          item.status
        ) &&
        item.reviewable === (item.status === 'requires_owner_review')
    )
  )
}

export function validAdoption(
  value: SourceAdoptionDescriptor | undefined,
  view: SourceReviewInspection | undefined
): value is SourceAdoptionDescriptor {
  if (
    !value ||
    !view ||
    view.state !== 'approved' ||
    !isUuid(view.decision_id ?? '') ||
    !exactKeys(value, [
      'client_id',
      'review_id',
      'decision_id',
      'source_document_id',
      'source_snapshot_sha256',
      'selection_sha256',
      'metadata',
    ]) ||
    value.client_id !== view.client_id ||
    value.review_id !== view.review_id ||
    value.decision_id !== view.decision_id ||
    !isUuid(value.source_document_id) ||
    value.source_snapshot_sha256 !== view.source_snapshot_sha256 ||
    value.selection_sha256 !== view.selection_sha256 ||
    !value.metadata ||
    !exactKeys(value.metadata, ['classification', 'service', 'category', 'financial_year'])
  )
    return false
  return (['classification', 'service', 'category', 'financial_year'] as const).every(
    key => value.metadata[key] === view.selection[key]
  )
}

export function validCopyReceipt(
  value: ReviewedCopyReceipt | undefined,
  client: string
): value is ReviewedCopyReceipt {
  return (
    !!value &&
    exactKeys(value, [
      'operation_id',
      'client_id',
      'resource_id',
      'version_id',
      'scan_job_id',
      'state',
    ]) &&
    value.client_id === client &&
    value.state === 'pending_scan' &&
    ['operation_id', 'client_id', 'resource_id', 'version_id', 'scan_job_id'].every(key =>
      isUuid(value[key as keyof ReviewedCopyReceipt])
    )
  )
}

export const isUuid = (value: string) =>
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value)
export const reviewsPath = (client: string) => `${filePath(client)}/source-reviews`
export const reviewPath = (client: string, review: string) => {
  if (!isUuid(review)) throw new Error('A valid source review identifier is required')
  return `${reviewsPath(client)}/${review}`
}
export const reviewReasons: Record<ReviewReason, string> = {
  ambiguous_identity: 'Clarify the client record',
  metadata_correction: 'Correct document details',
  undated: 'Confirm an undated document',
  classification_review: 'Review document ownership',
}
export const serviceLabels: Record<VaultService, string> = {
  income_tax: 'Income tax',
  gst: 'GST',
  finance: 'Finance',
}
export const categoryLabels: Record<string, string> = {
  pan_card: 'PAN card',
  aadhaar_card: 'Aadhaar card',
  passport: 'Passport',
  form_16: 'Form 16',
  form_16a: 'Form 16A',
  form_26as: 'Form 26AS',
  ais: 'AIS',
  tis: 'TIS',
  bank_statement: 'Bank statement',
  salary_slip: 'Salary slip',
  interest_certificate: 'Interest certificate',
  investment_proof: 'Investment proof',
  capital_gain_statement: 'Capital-gains statement',
  property_deed: 'Property deed',
  property_registration: 'Property registration',
  home_loan_interest_certificate: 'Home-loan interest certificate',
  home_loan_principal: 'Home-loan principal',
  demand_notice: 'Tax demand notice',
  assessment_order: 'Assessment order',
  rent_receipt: 'Rent receipt',
  insurance_receipt: 'Insurance receipt',
  gst_registration_certificate: 'GST registration certificate',
  itr_acknowledgement: 'ITR acknowledgement',
  itr_filed_copy: 'Filed ITR copy',
  itr_v_form: 'ITR-V form',
  gst_return_copy: 'GST return copy',
  gst_challan: 'GST challan',
  gst_annual_return: 'GST annual return',
  approved_deliverable: 'Approved deliverable',
}
export const outputCategories = [
  'itr_acknowledgement',
  'itr_filed_copy',
  'itr_v_form',
  'gst_return_copy',
  'gst_challan',
  'gst_annual_return',
  'approved_deliverable',
]
export const categoriesFor = (classification: SourceReviewSelection['classification']) =>
  classification === 'client_output'
    ? outputCategories
    : Object.keys(categoryLabels).filter(value => value !== 'approved_deliverable')
export const exactFinancialYear = (year: string) =>
  /^20\d{2}-\d{2}$/.test(year) && Number(year.slice(-2)) === (Number(year.slice(0, 4)) + 1) % 100

/** Only a fresh response for this exact client/review can supply a decision. */
export function validInspection(
  value: SourceReviewInspection | undefined,
  client: string,
  review: string
): value is SourceReviewInspection {
  if (
    !value ||
    value.client_id !== client ||
    value.review_id !== review ||
    !isUuid(review) ||
    (value.state === 'pending' ? value.decision_id !== null : !isUuid(value.decision_id ?? '')) ||
    !['pending', 'approved', 'rejected', 'cancelled', 'revoked'].includes(value.state) ||
    !/^[a-f0-9]{64}$/.test(value.selection_sha256) ||
    !/^[a-f0-9]{64}$/.test(value.source_snapshot_sha256)
  )
    return false
  const selection = value.selection
  return (
    !!selection &&
    isUuid(selection.source_alias_id) &&
    ['individual', 'business'].includes(selection.customer_type) &&
    Number.isInteger(selection.customer_id) &&
    selection.customer_id > 0 &&
    selection.customer_id <= 2147483647 &&
    ['client_controlled', 'client_output'].includes(selection.classification) &&
    ['income_tax', 'gst', 'finance'].includes(selection.service) &&
    categoriesFor(selection.classification).includes(selection.category) &&
    (selection.period_basis === 'undated'
      ? selection.financial_year === null
      : selection.period_basis === 'financial_year' &&
        typeof selection.financial_year === 'string' &&
        exactFinancialYear(selection.financial_year)) &&
    ['ambiguous_identity', 'metadata_correction', 'undated', 'classification_review'].includes(
      selection.reason
    ) &&
    selection.confirm_nonprivate_source === true &&
    selection.confirm_client_ownership === true &&
    selection.terms_version === SOURCE_REVIEW_TERMS
  )
}
